import React, { useMemo, useState } from "react";
import "./ApplicationForm.css";

const MAX_MEMBERS = 6;
const MIN_MEMBERS = 3;

const AGE_OPTIONS = [
  "13 and under",
  ...Array.from({ length: 17 }, (_, i) => String(i + 14)),
  "30+",
];

const GENDER_OPTIONS = [
  "Male",
  "Female",
  "Prefer not to say",
];

const KRPC_PARTICIPATION_OPTIONS = [
  "This is my first time participating.",
  "This is my second time participating.",
  "This is my third time participating.",
];

const PROGRAMMING_OPTIONS = [
  {
    value: "none",
    label: "None",
    description: "I'm completely new to this!",
  },
  {
    value: "minimal",
    label: "Minimal",
    description:
      "I understand how computer programming works, and I have written a few lines of code in at least one computer language.",
  },
  {
    value: "proficient",
    label: "Proficient",
    description:
      "I understand syntax and can write my own code in at least one computer language. I am able to troubleshoot and debug my own code most of the time.",
  },
  {
    value: "highly_skilled",
    label: "Highly skilled",
    description:
      "I have created my own computer/mobile applications and/or taken several classes in computer science.",
  },
];

const COMPETITION_OPTIONS = [
  "None",
  "1-2",
  "3 or more",
];

const emptyMember = () => ({
  firstName: "",
  lastName: "",
  age: "",
  gender: "",
  schoolName: "",
  schoolCity: "",
  originalCity: "",
  nationality: "",
  email: "",
  whatsapp: "",
  krpcParticipation: "",
  programmingExperience: "",
  competitionExperience: "",
});

const createInitialForm = () => ({
  email: "",
  course: "",
  teamName: "",
  members: [
    emptyMember(),
    emptyMember(),
    emptyMember(),
  ],
  agreements: {
    representation: false,
    media: false,
  },
  notes: "",
});

function ProgressBar({ currentStep, totalSteps }) {
  const percentage = Math.round((currentStep / totalSteps) * 100);

  return (
    <div className="application-progress">
      <div className="progress-header">
        <span>
          Step {currentStep} of {totalSteps}
        </span>

        <span>{percentage}%</span>
      </div>

      <div className="progress-track">
        <div
          className="progress-value"
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
}

function TextInput({
  label,
  value,
  onChange,
  required = false,
  type = "text",
  placeholder = "",
  error = "",
}) {
  return (
    <div className={`form-field ${error ? "has-error" : ""}`}>
      <label>
        {label}
        {required && <span className="required">*</span>}
      </label>

      <input
        type={type}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
      />

      {error && <div className="field-error">{error}</div>}
    </div>
  );
}

function SelectInput({
  label,
  value,
  onChange,
  options,
  required = false,
  error = "",
}) {
  return (
    <div className={`form-field ${error ? "has-error" : ""}`}>
      <label>
        {label}
        {required && <span className="required">*</span>}
      </label>

      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
      >
        <option value="">Select an option</option>

        {options.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>

      {error && <div className="field-error">{error}</div>}
    </div>
  );
}

function RadioGroup({
  label,
  value,
  onChange,
  options,
  required = false,
  error = "",
}) {
  return (
    <div className={`form-field ${error ? "has-error" : ""}`}>
      <label className="question-label">
        {label}
        {required && <span className="required">*</span>}
      </label>

      <div className="radio-list">
        {options.map((option) => {
          const optionValue =
            typeof option === "string" ? option : option.value;

          const optionLabel =
            typeof option === "string" ? option : option.label;

          return (
            <label className="radio-option" key={optionValue}>
              <input
                type="radio"
                name={label}
                value={optionValue}
                checked={value === optionValue}
                onChange={() => onChange(optionValue)}
              />

              <span>{optionLabel}</span>

              {typeof option === "object" && option.description && (
                <small>{option.description}</small>
              )}
            </label>
          );
        })}
      </div>

      {error && <div className="field-error">{error}</div>}
    </div>
  );
}

function MemberForm({
  member,
  memberNumber,
  onChange,
  errors = {},
}) {
  const update = (field, value) => {
    onChange({
      ...member,
      [field]: value,
    });
  };

  return (
    <div className="member-card">
      <div className="member-card-header">
        <div>
          <span className="member-number">
            Team Member {memberNumber}
          </span>

          {memberNumber === 1 && (
            <span className="representative-badge">
              Team Representative
            </span>
          )}
        </div>
      </div>

      {memberNumber === 1 && (
        <div className="representative-note">
          Please elect one person from your team to serve as the
          team representative.
        </div>
      )}

      <div className="two-column">
        <TextInput
          label="First Name"
          required
          value={member.firstName}
          onChange={(value) => update("firstName", value)}
          error={errors.firstName}
        />

        <TextInput
          label="Last Name"
          required
          value={member.lastName}
          onChange={(value) => update("lastName", value)}
          error={errors.lastName}
        />
      </div>

      <SelectInput
        label="Age"
        required={memberNumber !== 6}
        value={member.age}
        onChange={(value) => update("age", value)}
        options={AGE_OPTIONS}
        error={errors.age}
      />

      <RadioGroup
        label="Gender"
        required
        value={member.gender}
        onChange={(value) => update("gender", value)}
        options={GENDER_OPTIONS}
        error={errors.gender}
      />

      <div className="two-column">
        <TextInput
          label="School Name"
          required
          value={member.schoolName}
          onChange={(value) => update("schoolName", value)}
          error={errors.schoolName}
        />

        <TextInput
          label="School City"
          required
          value={member.schoolCity}
          onChange={(value) => update("schoolCity", value)}
          error={errors.schoolCity}
        />
      </div>

      <TextInput
        label="What city are you originally from?"
        required
        value={member.originalCity}
        onChange={(value) => update("originalCity", value)}
        error={errors.originalCity}
      />

      <TextInput
        label="What is your nationality?"
        required
        value={member.nationality}
        onChange={(value) => update("nationality", value)}
        error={errors.nationality}
      />

      <TextInput
        label="What is your e-mail address?"
        required
        type="email"
        value={member.email}
        onChange={(value) => update("email", value)}
        error={errors.email}
      />

      <TextInput
        label="What is your WhatsApp Phone number?"
        required
        type="tel"
        value={member.whatsapp}
        onChange={(value) => update("whatsapp", value)}
        error={errors.whatsapp}
      />

      <RadioGroup
        label="How many times have you participated in the KRPC program?"
        required
        value={member.krpcParticipation}
        onChange={(value) =>
          update("krpcParticipation", value)
        }
        options={KRPC_PARTICIPATION_OPTIONS}
        error={errors.krpcParticipation}
      />

      <RadioGroup
        label="Which of the following best describes your computer programming experience?"
        required
        value={member.programmingExperience}
        onChange={(value) =>
          update("programmingExperience", value)
        }
        options={PROGRAMMING_OPTIONS}
        error={errors.programmingExperience}
      />

      <RadioGroup
        label="How many robotics or computer programming competitions have you participated in before?"
        required
        value={member.competitionExperience}
        onChange={(value) =>
          update("competitionExperience", value)
        }
        options={COMPETITION_OPTIONS}
        error={errors.competitionExperience}
      />
    </div>
  );
}

function validateMember(member) {
  const errors = {};

  const requiredFields = [
    ["firstName", "First name is required."],
    ["lastName", "Last name is required."],
    ["age", "Age is required."],
    ["gender", "Gender is required."],
    ["schoolName", "School name is required."],
    ["schoolCity", "School city is required."],
    ["originalCity", "Original city is required."],
    ["nationality", "Nationality is required."],
    ["email", "Email is required."],
    ["whatsapp", "WhatsApp number is required."],
    [
      "krpcParticipation",
      "KRPC participation information is required.",
    ],
    [
      "programmingExperience",
      "Programming experience is required.",
    ],
    [
      "competitionExperience",
      "Competition experience is required.",
    ],
  ];

  requiredFields.forEach(([field, message]) => {
    if (!member[field]?.trim()) {
      errors[field] = message;
    }
  });

  if (
    member.email &&
    !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(member.email)
  ) {
    errors.email = "Please enter a valid email address.";
  }

  return errors;
}

export default function ApplicationForm({
  onSubmit,
  initialData = null,
}) {
  const [form, setForm] = useState(
    initialData || createInitialForm()
  );

  const [currentStep, setCurrentStep] = useState(0);
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [saving, setSaving] = useState(false);

  const totalSteps = form.members.length + 3;

  const updateForm = (field, value) => {
    setForm((previous) => ({
      ...previous,
      [field]: value,
    }));
  };

  const updateMember = (index, member) => {
    setForm((previous) => ({
      ...previous,
      members: previous.members.map((item, i) =>
        i === index ? member : item
      ),
    }));
  };

  const addMember = () => {
    if (form.members.length >= MAX_MEMBERS) return;

    setForm((previous) => ({
      ...previous,
      members: [
        ...previous.members,
        emptyMember(),
      ],
    }));
  };

  const removeMember = (index) => {
    if (form.members.length <= MIN_MEMBERS) return;

    if (index < MIN_MEMBERS) return;

    setForm((previous) => ({
      ...previous,
      members: previous.members.filter(
        (_, i) => i !== index
      ),
    }));
  };

  const validateStep = () => {
    const newErrors = {};

    if (currentStep === 0) {
      if (!form.email.trim()) {
        newErrors.email = "Email is required.";
      } else if (
        !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)
      ) {
        newErrors.email = "Please enter a valid email address.";
      }

      if (!form.course) {
        newErrors.course = "Please select a course.";
      }
    }

    if (currentStep >= 1 && currentStep <= form.members.length) {
      const memberIndex = currentStep - 1;

      newErrors.member = validateMember(
        form.members[memberIndex]
      );
    }

    if (currentStep === form.members.length + 1) {
      if (!form.agreements.representation) {
        newErrors.representation =
          "You must agree to this statement.";
      }

      if (!form.agreements.media) {
        newErrors.media =
          "You must agree to this statement.";
      }
    }

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  const nextStep = () => {
    if (!validateStep()) return;

    setCurrentStep((step) =>
      Math.min(step + 1, totalSteps - 1)
    );

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const previousStep = () => {
    setErrors({});

    setCurrentStep((step) => Math.max(step - 1, 0));

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const saveDraft = async () => {
    setSaving(true);

    try {
      localStorage.setItem(
        "stemx365_krpc7_application",
        JSON.stringify(form)
      );

      if (typeof onSubmit === "function") {
        await onSubmit(form, {
          mode: "draft",
        });
      }
    } finally {
      setSaving(false);
    }
  };

  const submitApplication = async () => {
    if (!validateStep()) return;

    setSaving(true);

    try {
      if (typeof onSubmit === "function") {
        await onSubmit(form, {
          mode: "submit",
        });
      }

      localStorage.removeItem(
        "stemx365_krpc7_application"
      );

      setSubmitted(true);
    } finally {
      setSaving(false);
    }
  };

  const stepTitle = useMemo(() => {
    if (currentStep === 0) {
      return "Team Information";
    }

    if (
      currentStep >= 1 &&
      currentStep <= form.members.length
    ) {
      return `Team Member ${currentStep}`;
    }

    if (currentStep === form.members.length + 1) {
      return "STEMX365 Representation";
    }

    return "Review & Submit";
  }, [currentStep, form.members.length]);

  if (submitted) {
    return (
      <div className="application-page">
        <div className="application-success">
          <div className="success-icon">✓</div>

          <h1>Application Submitted</h1>

          <p>
            Thank you for completing the KRPC7 application
            through STEMX365.
          </p>

          <p>
            We will contact you about the status of your
            application.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="application-page">
      <div className="application-container">

        <header className="application-header">
          <div className="brand">
            <div className="brand-mark">S</div>

            <div>
              <strong>STEMX365</strong>
              <span>Kibo Robot Programming Challenge</span>
            </div>
          </div>

          <div className="application-badge">
            KRPC7
          </div>
        </header>

        <section className="application-hero">
          <h1>
            KRPC7: Preliminary Phase Student
            Application
          </h1>

          <p>
            Bangladesh
          </p>

          <div className="deadline">
            Application deadline:
            <strong> September 9, 2026 — 11:59 EST</strong>
          </div>
        </section>

        <ProgressBar
          currentStep={currentStep + 1}
          totalSteps={totalSteps}
        />

        <main className="application-form-card">

          <div className="section-heading">
            <span className="step-label">
              STEP {currentStep + 1}
            </span>

            <h2>{stepTitle}</h2>
          </div>

          {/* STEP 0 */}
          {currentStep === 0 && (
            <>
              <div className="info-box">
                <h3>Before you begin</h3>

                <ul>
                  <li>
                    All required parts of the application
                    must be completed.
                  </li>

                  <li>
                    All team members must be enrolled
                    students.
                  </li>

                  <li>
                    Teams must contain at least three and
                    up to six members.
                  </li>

                  <li>
                    Each person may participate on only one
                    team.
                  </li>

                  <li>
                    All members should have a basic
                    understanding of Newtonian physics and
                    Euclidean geometry.
                  </li>
                </ul>
              </div>

              <TextInput
                label="Email"
                required
                type="email"
                value={form.email}
                onChange={(value) =>
                  updateForm("email", value)
                }
                error={errors.email}
              />

              <RadioGroup
                label="Regular course / Beginner course"
                required
                value={form.course}
                onChange={(value) =>
                  updateForm("course", value)
                }
                options={[
                  {
                    value: "Regular",
                    label: "Regular",
                  },
                  {
                    value: "Beginner",
                    label: "Beginner",
                  },
                ]}
                error={errors.course}
              />

              <TextInput
                label="Team Name"
                value={form.teamName}
                onChange={(value) =>
                  updateForm("teamName", value)
                }
              />

              <div className="community-box">
                <strong>Stay connected</strong>

                <p>
                  Please join the STEMX365 KRPC WhatsApp
                  community and follow STEMX365 Facebook
                  for updates.
                </p>

                <div className="community-links">
                  <span>WhatsApp Community</span>
                  <span>STEMX365 Facebook</span>
                </div>
              </div>
            </>
          )}

          {/* TEAM MEMBERS */}
          {currentStep >= 1 &&
            currentStep <= form.members.length && (
              <MemberForm
                member={
                  form.members[currentStep - 1]
                }
                memberNumber={currentStep}
                onChange={(member) =>
                  updateMember(
                    currentStep - 1,
                    member
                  )
                }
                errors={errors.member || {}}
              />
            )}

          {/* REPRESENTATION */}
          {currentStep === form.members.length + 1 && (
            <section className="agreements-section">

              <div className="info-box">
                <h3>STEMX365 Representation</h3>

                <p>
                  By registering for the JAXA Kibo Robot
                  Programming Challenge through STEMX365,
                  participants acknowledge that they will
                  represent STEMX365 in this program while
                  remaining students of their respective
                  schools, colleges, or universities.
                </p>

                <p>
                  STEMX365 will serve as the official
                  coordinating organization for their
                  participation.
                </p>
              </div>

              <label
                className={`checkbox-card ${
                  errors.representation
                    ? "has-error"
                    : ""
                }`}
              >
                <input
                  type="checkbox"
                  checked={
                    form.agreements.representation
                  }
                  onChange={(e) =>
                    setForm((previous) => ({
                      ...previous,
                      agreements: {
                        ...previous.agreements,
                        representation:
                          e.target.checked,
                      },
                    }))
                  }
                />

                <span>
                  I agree to the STEMX365 representation
                  statement above.
                </span>
              </label>

              {errors.representation && (
                <div className="field-error">
                  {errors.representation}
                </div>
              )}

              <label
                className={`checkbox-card ${
                  errors.media ? "has-error" : ""
                }`}
              >
                <input
                  type="checkbox"
                  checked={form.agreements.media}
                  onChange={(e) =>
                    setForm((previous) => ({
                      ...previous,
                      agreements: {
                        ...previous.agreements,
                        media: e.target.checked,
                      },
                    }))
                  }
                />

                <span>
                  I agree that any official video,
                  publication, media coverage, or
                  announcement related to my participation
                  in the Kibo Robot Programming Challenge
                  under the STEMX365 program will be
                  coordinated with and approved by STEMX365
                  before public release.
                </span>
              </label>

              {errors.media && (
                <div className="field-error">
                  {errors.media}
                </div>
              )}

              <div className="notes-field">
                <label>
                  Is there anything you would like to add
                  as a note about your team that you think
                  may be relevant?
                </label>

                <textarea
                  rows={6}
                  value={form.notes}
                  onChange={(e) =>
                    updateForm(
                      "notes",
                      e.target.value
                    )
                  }
                  placeholder="Optional"
                />
              </div>
            </section>
          )}

          {/* REVIEW */}
          {currentStep === totalSteps - 1 && (
            <section className="review-section">

              <div className="review-block">
                <h3>Team Information</h3>

                <ReviewRow
                  label="Email"
                  value={form.email}
                />

                <ReviewRow
                  label="Course"
                  value={form.course}
                />

                <ReviewRow
                  label="Team Name"
                  value={
                    form.teamName || "Not provided"
                  }
                />
              </div>

              {form.members.map((member, index) => (
                <div
                  className="review-block"
                  key={index}
                >
                  <h3>
                    Team Member {index + 1}
                    {index === 0
                      ? " — Representative"
                      : ""}
                  </h3>

                  <ReviewRow
                    label="Name"
                    value={`${member.firstName} ${member.lastName}`}
                  />

                  <ReviewRow
                    label="Age"
                    value={member.age}
                  />

                  <ReviewRow
                    label="Gender"
                    value={member.gender}
                  />

                  <ReviewRow
                    label="School"
                    value={`${member.schoolName}, ${member.schoolCity}`}
                  />

                  <ReviewRow
                    label="Original City"
                    value={member.originalCity}
                  />

                  <ReviewRow
                    label="Nationality"
                    value={member.nationality}
                  />

                  <ReviewRow
                    label="Email"
                    value={member.email}
                  />

                  <ReviewRow
                    label="WhatsApp"
                    value={member.whatsapp}
                  />

                  <ReviewRow
                    label="KRPC Participation"
                    value={
                      member.krpcParticipation
                    }
                  />

                  <ReviewRow
                    label="Programming"
                    value={
                      PROGRAMMING_OPTIONS.find(
                        (item) =>
                          item.value ===
                          member.programmingExperience
                      )?.label ||
                      member.programmingExperience
                    }
                  />

                  <ReviewRow
                    label="Competitions"
                    value={
                      member.competitionExperience
                    }
                  />
                </div>
              ))}

              <div className="review-block">
                <h3>Additional Information</h3>

                <ReviewRow
                  label="Notes"
                  value={
                    form.notes || "No additional notes."
                  }
                />
              </div>

              <div className="final-warning">
                <strong>
                  Please review your application carefully.
                </strong>

                <p>
                  Once submitted, your application will be
                  sent to STEMX365 for processing.
                </p>
              </div>
            </section>
          )}

          {/* ADD MEMBER */}
          {currentStep === form.members.length - 1 &&
            form.members.length < MAX_MEMBERS && (
              <div className="add-member-area">
                <button
                  type="button"
                  className="secondary-button"
                  onClick={addMember}
                >
                  + Add Another Team Member
                </button>

                <p>
                  Your team must have at least{" "}
                  {MIN_MEMBERS} members and can have up to{" "}
                  {MAX_MEMBERS} members.
                </p>
              </div>
            )}

          {/* REMOVE MEMBER */}
          {currentStep >= 4 &&
            currentStep <= form.members.length &&
            form.members.length > MIN_MEMBERS && (
              <button
                type="button"
                className="remove-member"
                onClick={() =>
                  removeMember(currentStep - 1)
                }
              >
                Remove this team member
              </button>
            )}

          <div className="form-actions">

            {currentStep > 0 && (
              <button
                type="button"
                className="back-button"
                onClick={previousStep}
              >
                ← Back
              </button>
            )}

            <button
              type="button"
              className="save-button"
              disabled={saving}
              onClick={saveDraft}
            >
              {saving ? "Saving..." : "Save Draft"}
            </button>

            {currentStep < totalSteps - 1 ? (
              <button
                type="button"
                className="primary-button"
                onClick={nextStep}
              >
                Continue →
              </button>
            ) : (
              <button
                type="button"
                className="primary-button submit-button"
                disabled={saving}
                onClick={submitApplication}
              >
                {saving
                  ? "Submitting..."
                  : "Submit Application"}
              </button>
            )}

          </div>
        </main>

        <footer className="application-footer">
          <p>
            KRPC7 Preliminary Phase — Bangladesh
          </p>

          <p>
            STEMX365 • Kibo Robot Programming Challenge
          </p>
        </footer>
      </div>
    </div>
  );
}

function ReviewRow({ label, value }) {
  return (
    <div className="review-row">
      <span>{label}</span>
      <strong>{value || "—"}</strong>
    </div>
  );
}